import { useMemo, useState } from 'react';
import { Dialog } from '../design/primitives/Overlay';
import { Button } from '../design/primitives/Button';
import { Banner, EmptyState } from '../design/primitives/Misc';
import { Icon } from '../design/icons';
import { AutoForm } from '../forms/AutoForm';
import { useBulk } from '../api/hooks';
import type { BulkResult } from '../api/client';
import type { FieldMeta } from '../meta/types';

// Bulk field edit: an AutoForm over the resource's editable fields, in edit mode with empty
// initial values so ONLY the fields the user fills are sent (dirty-only). Submits one call to
// POST /<resource>/bulk {op:'update', ids, data}; the backend runs each id in its own
// savepoint, so we render the per-id succeeded/failed breakdown before refetching.

export interface BulkEditDialogProps {
  resource: string;
  ids: string[];
  fields: FieldMeta[];
  label: string;
  labelPlural: string;
  onClose: () => void;
  /** Called after the user dismisses the results (so the list can refetch + clear selection). */
  onDone: () => void;
}

export function BulkEditDialog({ resource, ids, fields, label, labelPlural, onClose, onDone }: BulkEditDialogProps) {
  const bulk = useBulk(resource, { toast: { success: false, error: false } });
  const [result, setResult] = useState<BulkResult | null>(null);
  // Bulk patch never requires a field — you set only what you want to change.
  const optionalFields = useMemo(() => fields.map((f) => ({ ...f, required: false })), [fields]);

  const noun = ids.length === 1 ? label.toLowerCase() : labelPlural.toLowerCase();

  const finish = () => {
    onDone();
    onClose();
  };

  return (
    <Dialog
      open
      onClose={result ? finish : onClose}
      locked={bulk.isPending}
      title={result ? 'Bulk edit results' : `Edit ${ids.length} ${noun}`}
      description={result ? undefined : 'Fill only the fields you want to change on every selected record.'}
    >
      {result ? (
        <div className="hf-stack">
          <Banner tone={result.failed ? 'warn' : 'pos'} icon={result.failed ? 'alert-triangle' : 'check-circle'}>
            {result.succeeded} updated{result.failed ? `, ${result.failed} failed` : ''}.
          </Banner>
          {result.failed > 0 && (
            <ul className="hf-bulk-results" aria-label="Failed records">
              {result.results
                .filter((r) => !r.ok)
                .map((r) => (
                  <li key={r.id}>
                    <Icon name="x-circle" size={13} className="hf-neg" />
                    <code>{r.id.slice(0, 8)}</code>
                    <span>{r.error?.message ?? 'Failed'}</span>
                  </li>
                ))}
            </ul>
          )}
          <div className="hf-form-actions">
            <Button variant="primary" onClick={finish} data-autofocus>Done</Button>
          </div>
        </div>
      ) : fields.length === 0 ? (
        <EmptyState title="No editable fields" hint={`${label} has no fields you can bulk edit.`} icon="lock" />
      ) : (
        <AutoForm
          fields={optionalFields}
          mode="edit"
          initial={{}}
          busy={bulk.isPending}
          submitLabel={`Update ${ids.length} ${noun}`}
          onCancel={onClose}
          onSubmit={async (values) => {
            if (Object.keys(values).length === 0) {
              onClose();
              return;
            }
            const res = await bulk.mutateAsync({ op: 'update', ids, data: values });
            setResult(res);
          }}
        />
      )}
    </Dialog>
  );
}
